import { SupabaseClient } from '@supabase/supabase-js';
import { BaseRepository } from './base.repository';
import { ProjectInvitation, ProjectInvitationInsert, ProjectInvitationUpdate, InvitationStatus } from '../entities/project-invitation';
import { PaginationOptions, PaginatedResponse } from '../types/pagination.types';
import { applyPagination } from '../utils/pagination.utils';

interface ProjectInvitationWithProject {
  id: string;
  project_id: string;
  email: string;
  role: string;
  invited_by_id: string;
  status: InvitationStatus;
  expires_at: string;
  created_at: string;
  accepted_at: string | null;
  token: string;
  project: {
    id: string;
    name: string;
    description: string | null;
  };
}

export class ProjectInvitationRepository extends BaseRepository<ProjectInvitation, ProjectInvitationInsert, ProjectInvitationUpdate> {
  constructor(supabase: SupabaseClient) {
    super(supabase, 'project_invitations');
  }

  /**
   * Save a ProjectInvitation entity
   */
  async save(invitation: ProjectInvitation): Promise<ProjectInvitation> {
    if (!invitation.isDirty()) {
      return invitation;
    }

    const data = invitation.toData();
    const { id, created_at, token, invited_by_id, project_id, email, role, expires_at, ...updateData } = data;

    const { data: saved, error } = await this.supabase
      .from(this.tableName)
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;

    const savedInvitation = ProjectInvitation.fromData(saved);
    savedInvitation.markAsClean();
    return savedInvitation;
  }

  /**
   * Override findById to return ProjectInvitation entity
   */
  async findById(id: string): Promise<ProjectInvitation | null> {
    const { data, error } = await this.supabase
      .from(this.tableName)
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    
    return data ? ProjectInvitation.fromData(data) : null;
  }

  /**
   * Find invitation by token
   */
  async findByToken(token: string): Promise<ProjectInvitation | null> {
    const { data, error } = await this.supabase
      .from(this.tableName)
      .select('*')
      .eq('token', token)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    
    return data ? ProjectInvitation.fromData(data) : null;
  }

  /**
   * Override create to return ProjectInvitation entity
   */
  async create(data: ProjectInvitationInsert): Promise<ProjectInvitation> {
    const { data: created, error } = await this.supabase
      .from(this.tableName)
      .insert(data)
      .select()
      .single();

    if (error) throw error;
    return ProjectInvitation.fromData(created);
  }

  /**
   * Find invitations by project (excluding expired ones)
   */
  async findByProject(projectId: string, status?: InvitationStatus): Promise<ProjectInvitation[]> {
    let query = this.supabase
      .from(this.tableName)
      .select('*')
      .eq('project_id', projectId)
      .gt('expires_at', new Date().toISOString()) // Exclude expired invitations
      .order('created_at', { ascending: false });

    if (status) {
      query = query.eq('status', status);
    }

    const { data, error } = await query;
    if (error) throw error;
    return (data || []).map(d => ProjectInvitation.fromData(d));
  }

  /**
   * Find invitations by project (paginated, excluding expired ones)
   */
  async findByProjectPaginated(
    projectId: string, 
    status?: InvitationStatus,
    options?: PaginationOptions
  ): Promise<PaginatedResponse<ProjectInvitation>> {
    let query = this.supabase
      .from(this.tableName)
      .select('*')
      .eq('project_id', projectId)
      .gt('expires_at', new Date().toISOString()); // Exclude expired invitations

    if (status) {
      query = query.eq('status', status);
    }

    const finalOptions = {
      ...options,
      orderBy: options?.orderBy || 'created_at',
      order: options?.order || 'desc' as const
    };

    const result = await applyPagination(query, finalOptions, (d) => ProjectInvitation.fromData(d));
    if ('pagination' in result) {
      return result;
    }
    throw new Error('Expected paginated response');
  }

  /**
   * Find invitations by email (excluding expired ones)
   */
  async findByEmail(email: string, status?: InvitationStatus): Promise<ProjectInvitation[]> {
    let query = this.supabase
      .from(this.tableName)
      .select('*')
      .eq('email', email)
      .gt('expires_at', new Date().toISOString()) // Exclude expired invitations
      .order('created_at', { ascending: false });

    if (status) {
      query = query.eq('status', status);
    }

    const { data, error } = await query;
    if (error) throw error;
    return (data || []).map(d => ProjectInvitation.fromData(d));
  }

  /**
   * Find invitations by email with project details (excluding expired ones)
   */
  async findByEmailWithProject(email: string, status?: InvitationStatus): Promise<ProjectInvitationWithProject[]> {
    let query = this.supabase
      .from(this.tableName)
      .select(`
        *,
        project:project_id (
          id,
          name,
          description
        )
      `)
      .eq('email', email)
      .gt('expires_at', new Date().toISOString()) // Exclude expired invitations
      .order('created_at', { ascending: false });

    if (status) {
      query = query.eq('status', status);
    }

    const { data, error } = await query;
    if (error) throw error;
    return (data || []) as ProjectInvitationWithProject[];
  }

  /**
   * Find pending invitation for email and project
   */
  async findPendingInvitation(projectId: string, email: string): Promise<ProjectInvitation | null> {
    const { data, error } = await this.supabase
      .from(this.tableName)
      .select('*')
      .eq('project_id', projectId)
      .eq('email', email)
      .eq('status', 'pending')
      .maybeSingle();

    if (error) throw error;
    return data ? ProjectInvitation.fromData(data) : null;
  }

  /**
   * Find the latest invitation for email and project (regardless of status or expiry)
   */
  async findLatestByProjectAndEmail(projectId: string, email: string): Promise<ProjectInvitation | null> {
    const { data, error } = await this.supabase
      .from(this.tableName)
      .select('*')
      .eq('project_id', projectId)
      .eq('email', email)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error && error.code !== 'PGRST116') throw error;
    return data ? ProjectInvitation.fromData(data) : null;
  }

  /**
   * Delete an invitation by ID
   */
  async deleteById(invitationId: string): Promise<void> {
    const { error } = await this.supabase
      .from(this.tableName)
      .delete()
      .eq('id', invitationId);

    if (error) throw error;
  }

  /**
   * Cancel invitation
   */
  async cancelInvitation(invitationId: string): Promise<void> {
    const invitation = await this.findById(invitationId);
    if (!invitation) throw new Error('Invitation not found');
    
    if (invitation.status !== 'pending') {
      throw new Error('Only pending invitations can be cancelled');
    }

    invitation.decline();
    await this.save(invitation);
  }

  /**
   * Expire old invitations
   */
  async expireOldInvitations(): Promise<number> {
    const { data, error } = await this.supabase
      .rpc('expire_old_invitations');

    if (error) throw error;
    return data || 0;
  }

  /**
   * Get invitations with user details (excluding expired ones)
   */
  async findByProjectWithDetails(projectId: string, status?: InvitationStatus): Promise<any[]> {
    let query = this.supabase
      .from(this.tableName)
      .select(`
        *,
        invited_by:invited_by_id (
          id,
          email,
          full_name,
          avatar_url
        )
      `)
      .eq('project_id', projectId)
      .gt('expires_at', new Date().toISOString()) // Exclude expired invitations
      .order('created_at', { ascending: false });

    if (status) {
      query = query.eq('status', status);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  }
}